import { getENV } from '@libs/common-next';
import type { AddItemRequestBody } from '@libs/common-node';
import type { CustomAddress, Product, TailoredApiResponse } from '@libs/common-types';
import type { Country } from '@libs/core';
import { LOCALIZED } from '@libs/core/constants';

import { getAutoshipData } from '../../product-details';
import type { MiniPdpForm } from '../types';
import { isMethodAvailableForPurchaseOption } from './is-method-available-for-purchase-option';

type GetMiniPdpAddItemRequestProps = Readonly<{
    customAddress?: CustomAddress | null;
    formData: MiniPdpForm;
    product: Product;
    tailored: TailoredApiResponse['data'] | null;
}>;

const hasCompleteAddress = (address?: CustomAddress | null) =>
    Boolean(
        address?.streetLine1.trim() &&
            address.city.trim() &&
            address.stateProvince.trim() &&
            address.zipPostalCode.trim()
    );

export function getMiniPdpAddItemRequest({
    customAddress,
    formData,
    product,
    tailored,
}: GetMiniPdpAddItemRequestProps): AddItemRequestBody | null {
    const { selectedSku, fulfilmentMethod, quantity, purchaseOption, autoshipFrequency, autoshipUnit } = formData;

    if (!selectedSku) return null;

    const method = fulfilmentMethod.toUpperCase() as keyof Product['deliveryMethods'];

    if (!isMethodAvailableForPurchaseOption(product, method, purchaseOption)) {
        return null;
    }

    const availableStock = tailored?.variants[selectedSku]?.[method]?.availableStock;
    const minimumQuantity = product.deliveryMethods[method].purchaseQuantities?.min ?? 1;

    if (availableStock != null && availableStock < minimumQuantity) {
        return null;
    }

    const country = getENV('NEXT_PUBLIC_LOCALE').split('-')[1] as Country;
    const autoshipData =
        purchaseOption === 'autoship' ? getAutoshipData({ frequency: autoshipFrequency, unit: autoshipUnit }) : {};

    if (fulfilmentMethod === 'bopis') {
        const storeId = tailored?.variants[selectedSku]?.BOPIS?.storeId;
        const offersBopis = storeId ? (tailored?.bopisStoreData?.[storeId]?.offersBopis ?? true) : false;

        if (!storeId || !offersBopis) return null;

        return {
            country,
            sku: selectedSku,
            quantity: quantity,
            fulfillmentMethod: fulfilmentMethod,
            storeId: storeId.padStart(4, '0'),
            ...autoshipData,
        };
    }

    if (fulfilmentMethod === 'sdd') {
        const sdd = tailored?.variants[selectedSku]?.SDD;

        if (
            !sdd?.doorDashValidationSuccess ||
            !sdd.storeId ||
            sdd.availableStock == null ||
            sdd.availableStock < minimumQuantity ||
            !customAddress ||
            !hasCompleteAddress(customAddress)
        ) {
            return null;
        }

        return {
            country,
            sku: selectedSku,
            quantity,
            fulfillmentMethod: fulfilmentMethod,
            storeId: sdd.storeId.padStart(4, '0'),
            dropOffAddress: {
                addressLine1: customAddress.streetLine1,
                addressLine2: customAddress.streetLine2,
                city: customAddress.city,
                stateProvince: customAddress.stateProvince,
                postalCode: customAddress.zipPostalCode,
                country,
            },
            ...autoshipData,
        };
    }

    return {
        country,
        sku: selectedSku,
        quantity: quantity,
        fulfillmentMethod: fulfilmentMethod,
        storeId: LOCALIZED.webInventory,
        ...autoshipData,
    };
}
